// src\themes\lino\components\Thirdgrid.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

interface ImageInfo {
    url: string;
}

interface GridItem {
    title?: string | null;
    buttonContent?: string | null;
    link?: string | null;
    image: ImageInfo;
}

interface ThirdgridProps {
    data?: {
        items?: GridItem[];
    };
}

export default function Thirdgrid({ data }: ThirdgridProps) {
    if (!data || !data.items || data.items.length < 3) return null;

    const items = data.items;

    const renderCard = (item: GridItem, aspectRatioClass = 'aspect-video') => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={`relative w-full overflow-hidden rounded-2xl group bg-primary-100 border border-transparent hover:border-primary-200 transition-colors duration-500 ${aspectRatioClass}`}
        >
            {item.image && (
                <Image
                    src={item.image.url}
                    alt={item.title || 'image'}
                    fill
                    className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.035]"
                />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/50 via-transparent to-transparent" />
            {(item.title || item.buttonContent) && (
                <motion.div
                    className="absolute bottom-0 right-0 space-y-2 p-7 w-full flex flex-col items-start text-primary-0 z-10"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.4 }}
                    variants={textGroupVariants}
                >
                    {item.title && (
                        <motion.h3 variants={textItemVariants} className="text-xl font-light tracking-tight">{item.title}</motion.h3>
                    )}
                    {item.buttonContent && (
                        <motion.div variants={textItemVariants}>
                            <Link
                                href={item.link || '#'}
                                className="inline-block border-b border-primary-0/70 text-[11px] uppercase tracking-[0.14em] pb-1"
                            >
                                {item.buttonContent}
                            </Link>
                        </motion.div>
                    )}
                </motion.div>
            )}
        </motion.div>
    );

    return (
        <div className="col-span-12 grid grid-cols-1 md:grid-cols-5 gap-5">
            <div className="col-span-1 md:col-span-3">
                {renderCard(items[0], 'aspect-video md:h-full')}
            </div>
            <div className="grid grid-rows-2 col-span-1 md:col-span-2 gap-4">
                <div className="h-full">{renderCard(items[1])}</div>
                <div className="h-full">{renderCard(items[2])}</div>
            </div>
        </div>
    );
}